/**
 * useTimeAnalysis Hook
 *
 * Analyzes trading performance by time - hour of day, day of week, and trading session.
 */

import { useMemo } from "react";
import type {
  CompletedTrade,
  TimeAnalysis,
  HourlyData,
  DayOfWeekData,
  SessionData,
  TradingSession,
} from "@nordex/core";

const DAY_NAMES: DayOfWeekData["day"][] = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

const SESSION_CONFIG: Record<
  TradingSession,
  { label: string; hourRange: string; startHour: number; endHour: number }
> = {
  asia: {
    label: "Asia",
    hourRange: "00:00 - 08:00 UTC",
    startHour: 0,
    endHour: 8,
  },
  europe: {
    label: "Europe",
    hourRange: "08:00 - 16:00 UTC",
    startHour: 8,
    endHour: 16,
  },
  us: {
    label: "US",
    hourRange: "14:00 - 22:00 UTC",
    startHour: 14,
    endHour: 22,
  },
};

/**
 * Get trading session for a given UTC hour
 * Note: Sessions can overlap (e.g., EU/US overlap 14:00-16:00)
 * For simplicity, we assign to the primary session
 */
function getSession(hour: number): TradingSession {
  if (hour >= 0 && hour < 8) return "asia";
  if (hour >= 8 && hour < 16) return "europe";
  return "us"; // 16-24 and overlap
}

/**
 * Calculate time-based analytics
 */
function calculateTimeAnalysis(trades: CompletedTrade[]): TimeAnalysis {
  // Initialize hourly data (0-23)
  const hourlyMap = new Map<
    number,
    { pnl: number; trades: number; wins: number }
  >();
  for (let h = 0; h < 24; h++) {
    hourlyMap.set(h, { pnl: 0, trades: 0, wins: 0 });
  }

  // Initialize daily data (Sun-Sat)
  const dailyMap = new Map<
    number,
    { pnl: number; trades: number; wins: number }
  >();
  for (let d = 0; d < 7; d++) {
    dailyMap.set(d, { pnl: 0, trades: 0, wins: 0 });
  }

  // Initialize session data
  const sessionMap = new Map<
    TradingSession,
    { pnl: number; trades: number; wins: number }
  >();
  (["asia", "europe", "us"] as TradingSession[]).forEach((s) => {
    sessionMap.set(s, { pnl: 0, trades: 0, wins: 0 });
  });

  // Process trades
  for (const trade of trades) {
    const hour = trade.entryTime.getUTCHours();
    const day = trade.entryTime.getUTCDay();
    const session = getSession(hour);
    const isWin = trade.netPnl > 0;

    // Update hourly
    const hourData = hourlyMap.get(hour)!;
    hourData.pnl += trade.netPnl;
    hourData.trades++;
    if (isWin) hourData.wins++;

    // Update daily
    const dayData = dailyMap.get(day)!;
    dayData.pnl += trade.netPnl;
    dayData.trades++;
    if (isWin) dayData.wins++;

    // Update session
    const sessionData = sessionMap.get(session)!;
    sessionData.pnl += trade.netPnl;
    sessionData.trades++;
    if (isWin) sessionData.wins++;
  }

  // Convert to arrays with win rates
  const hourly: HourlyData[] = [];
  let bestHour: HourlyData | null = null;
  let worstHour: HourlyData | null = null;

  for (let h = 0; h < 24; h++) {
    const data = hourlyMap.get(h)!;
    const hourData: HourlyData = {
      hour: h,
      pnl: data.pnl,
      trades: data.trades,
      wins: data.wins,
      winRate: data.trades > 0 ? (data.wins / data.trades) * 100 : 0,
    };
    hourly.push(hourData);

    if (data.trades > 0) {
      if (!bestHour || data.pnl > bestHour.pnl) bestHour = hourData;
      if (!worstHour || data.pnl < worstHour.pnl) worstHour = hourData;
    }
  }

  const daily: DayOfWeekData[] = [];
  let bestDay: DayOfWeekData | null = null;
  let worstDay: DayOfWeekData | null = null;

  for (let d = 0; d < 7; d++) {
    const data = dailyMap.get(d)!;
    const dayData: DayOfWeekData = {
      day: DAY_NAMES[d],
      dayIndex: d,
      pnl: data.pnl,
      trades: data.trades,
      wins: data.wins,
      winRate: data.trades > 0 ? (data.wins / data.trades) * 100 : 0,
    };
    daily.push(dayData);

    if (data.trades > 0) {
      if (!bestDay || data.pnl > bestDay.pnl) bestDay = dayData;
      if (!worstDay || data.pnl < worstDay.pnl) worstDay = dayData;
    }
  }

  const sessions: SessionData[] = (
    ["asia", "europe", "us"] as TradingSession[]
  ).map((session) => {
    const data = sessionMap.get(session)!;
    const config = SESSION_CONFIG[session];
    return {
      session,
      label: config.label,
      hourRange: config.hourRange,
      pnl: data.pnl,
      trades: data.trades,
      wins: data.wins,
      winRate: data.trades > 0 ? (data.wins / data.trades) * 100 : 0,
    };
  });

  return {
    hourly,
    daily,
    sessions,
    bestHour,
    worstHour,
    bestDay,
    worstDay,
  };
}

/**
 * Hook for time-based trading analysis
 */
export function useTimeAnalysis(trades: CompletedTrade[]): TimeAnalysis {
  return useMemo(() => calculateTimeAnalysis(trades), [trades]);
}

/**
 * Format hour for display
 */
export function formatHour(hour: number): string {
  const h = hour % 12 || 12;
  const ampm = hour < 12 ? "AM" : "PM";
  return `${h}${ampm}`;
}

/**
 * Format hour range
 */
export function formatHourRange(hour: number): string {
  const start = hour.toString().padStart(2, "0");
  const end = ((hour + 1) % 24).toString().padStart(2, "0");
  return `${start}:00-${end}:00`;
}

